import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BookmarkSimple, Heart, PencilSimple, UploadSimple, EyeSlash, Eye, LinkSimple, Check, BookOpen,
  Star, ChatCircle, Confetti, Camera, Cake, Trophy, Lock, ChatCircleText, ImageSquare, Calendar,
} from "@phosphor-icons/react";
import BannerPicker from "@/components/BannerPicker";
import AchievementsDialog from "@/components/AchievementsDialog";
import FollowListDialog from "@/components/FollowListDialog";
import CollectionStats from "@/components/CollectionStats";

import ReadingGoalCard from "@/components/ReadingGoalCard";
import SocialLinks from "@/components/SocialLinks";
import {
  LevelSidebarCard, NextTitleCard, ConquistasSidebarCard, EstatisticasCard,
  CollectionCard, RecentAchievements, SectionTitle,
  timeAgo, ACTIVITY_ICONS, ProfileBanner,
} from "@/components/ProfileWidgets";
import { Gear } from "@phosphor-icons/react";


export default function Profile() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [collection, setCollection] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [readList, setReadList] = useState([]);
  const [progress, setProgress] = useState(null);
  const [activity, setActivity] = useState([]);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [topicCount, setTopicCount] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [followListType, setFollowListType] = useState(null);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [form, setForm] = useState({ name: "", picture: "", bio: "", is_private: false, instagram: "", youtube: "", facebook: "", twitter: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) { navigate("/login"); return; }
    if (user) {
      setForm({
        name: user.name || "", picture: user.picture || "", bio: user.bio || "",
        is_private: !!user.is_private,
        instagram: user.instagram || "", youtube: user.youtube || "",
        facebook: user.facebook || "", twitter: user.twitter || "",
      });
      Promise.all([
        api.get("/me/collection").then(r => setCollection(r.data)),
        api.get("/me/wishlist").then(r => setWishlist(r.data)),
        api.get("/me/read").then(r => setReadList(r.data)),
        api.get("/me/progress").then(r => setProgress(r.data)),
        api.get("/me/activity").then(r => setActivity(r.data)),
        api.get(`/members/${user.user_id}`).then(r => {
          setFollowerCount(r.data.follower_count || 0);
          setFollowingCount(r.data.following_count || 0);
          setTopicCount(r.data.stats?.topics || 0);
        }).catch(() => {}),
      ]);
    }
  }, [user, loading, navigate]);

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/me", {
        name: form.name, picture: form.picture, bio: form.bio, is_private: form.is_private,
        instagram: form.instagram, youtube: form.youtube, facebook: form.facebook, twitter: form.twitter,
      });
      setUser(data);
      toast.success("Perfil atualizado");
      setEditOpen(false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function uploadFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Apenas imagens"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Imagem demasiado grande (máx 5MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setSaving(true);
    try {
      const { data } = await api.post("/me/upload-picture", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      setForm({ ...form, picture: data.picture });
      toast.success("Foto carregada");
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Falha no upload"); }
    finally { setSaving(false); e.target.value = ""; }
  }

  if (!user) return null;

  return (
    <div className="pt-16 pb-24 max-w-6xl mx-auto px-3 lg:px-6" data-testid="profile-page">
      {/* ============ BANNER ============ */}
      <div className="relative rounded-2xl overflow-hidden border border-white/10 mb-0" data-testid="profile-banner">
        <div className="aspect-[16/5] bg-gradient-to-br from-[#3f0d16] via-[#1a0a1f] to-[#0a0a10] relative">
          {user.banner_url ? (
            <img src={resolveImageUrl(user.banner_url)} alt="Banner" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%3E%3Cpath%20fill%3D%22%23ffffff08%22%20d%3D%22M30%205L55%2030L30%2055L5%2030z%22%2F%3E%3C%2Fsvg%3E')] opacity-70" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
        </div>
        <div className="absolute top-4 right-4 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setBannerOpen(true)} className="bg-black/40 backdrop-blur border-white/20 text-white hover:bg-black/60" data-testid="change-banner-btn">
            <Camera size={14} weight="bold" className="mr-1.5" /> Editar Capa
          </Button>
        </div>
      </div>

      {/* ============ IDENTITY BAR ============ */}
      <div className="relative -mt-16 md:-mt-20 px-2 md:px-6 mb-4">
        <div className="flex flex-col md:flex-row md:items-end gap-5">
          <div className="flex-shrink-0">
            {user.picture ? (
              <img src={resolveImageUrl(user.picture)} alt="" className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-[#0a0a0f] bg-[#0a0a0f]" />
            ) : (
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-5xl font-display font-bold border-4 border-[#0a0a0f]">
                {user.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 pb-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="profile-name">{user.name || "Sem nome"}</h1>
              {user.role === "admin" && (
                <span className="px-2 py-0.5 rounded-md bg-[#E63946] text-white text-[10px] font-mono-accent font-bold tracking-wider">ADMIN</span>
              )}
              {user.is_private && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-700/40 text-zinc-300 text-xs font-mono-accent" data-testid="private-badge">
                  <EyeSlash size={12} weight="bold" /> PRIVADO
                </span>
              )}
            </div>
            <div className="text-sm text-zinc-400 mt-1">
              Membro desde {user.created_at ? new Date(user.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" }) : "—"}
            </div>
            {user.bio && <p className="mt-3 text-zinc-300 leading-relaxed max-w-2xl whitespace-pre-line text-sm" data-testid="profile-bio-display">{user.bio}</p>}
            <SocialLinks user={user} className="mt-3" />
          </div>
          {/* Right-side actions */}
          <div className="flex items-center gap-2 pb-2 flex-shrink-0">
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="btn-crimson" data-testid="edit-profile-btn">
                  <PencilSimple size={14} weight="bold" className="mr-1.5" /> Editar Perfil
                </Button>
              </DialogTrigger>
              <EditProfileDialog form={form} setForm={setForm} onSubmit={saveProfile} saving={saving} uploadFile={uploadFile} user={user} setUser={setUser} />
            </Dialog>
            <button onClick={() => setEditOpen(true)} className="w-9 h-9 rounded-md border border-white/15 grid place-items-center text-zinc-300 hover:bg-white/5" data-testid="settings-btn" title="Definições">
              <Gear size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* ============ TABS ============ */}
      <div className="mb-8 border-b border-white/5 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max" data-testid="profile-tabs">
          <ProfileTab label="Perfil" active testid="tab-perfil" />
          <ProfileTab label="Comprados" onClick={() => navigate("/perfil/lista/collection")} testid="tab-comprados" />
          <ProfileTab label="Desejos" onClick={() => navigate("/perfil/lista/wishlist")} testid="tab-desejos" />
          <ProfileTab label="Já lidos" onClick={() => navigate("/perfil/lista/read")} testid="tab-lidos" />
          <ProfileTab label="Atividade" onClick={() => document.getElementById("activity")?.scrollIntoView({ behavior: "smooth" })} testid="tab-atividade" />
          <ProfileTab label="Seguidores" badge={followerCount} onClick={() => setFollowListType("followers")} testid="tab-followers" />
          <ProfileTab label="A seguir" badge={followingCount} onClick={() => setFollowListType("following")} testid="tab-following" />
        </div>
      </div>

      {/* ============ 2-COLUMN CONTENT ============ */}
      <div className="grid lg:grid-cols-[280px_1fr] gap-5">
        {/* LEFT SIDEBAR */}
        <aside className="space-y-4">
          {progress && <LevelSidebarCard progress={progress} />}
          <ReadingGoalCard />
          <NextTitleCard user={user} />
          {progress && <ConquistasSidebarCard progress={progress} onSeeAll={() => setAchievementsOpen(true)} />}
          <EstatisticasCard items={[
            { icon: <BookmarkSimple size={14} weight="fill" />, label: "Coleção", value: collection.length },
            { icon: <Heart size={14} weight="fill" />, label: "Desejos", value: wishlist.length },
            { icon: <BookOpen size={14} weight="fill" />, label: "Lidos", value: readList.length },
            { icon: <ChatCircleText size={14} weight="fill" />, label: "Tópicos", value: topicCount },
            { icon: <Star size={14} weight="fill" />, label: "Avaliações", value: progress?.counters.ratings ?? 0 },
            { icon: <ChatCircle size={14} weight="fill" />, label: "Comentários", value: progress?.counters.comments ?? 0 },
            { icon: <Heart size={14} weight="fill" />, label: "Likes em eventos", value: progress?.counters.event_likes ?? 0 },
            { icon: <Cake size={14} weight="fill" />, label: "Dias como membro", value: progress?.counters.membership_days ?? 0 },
            { icon: <Calendar size={14} weight="fill" />, label: "Login seguido", value: `${user.login_streak || 0} dias` },
          ]} />
        </aside>

        {/* MAIN CONTENT */}
        <div className="space-y-6">
          {/* Collections preview */}
          <div>
            <SectionTitle title="A TUA COLEÇÃO" more={{ label: "Ver coleção completa", to: "/perfil/lista/collection" }} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CollectionCard title="Comprados" icon={<BookmarkSimple size={18} weight="fill" />} color="#E63946" items={collection} to="/perfil/lista/collection" />
              <CollectionCard title="Desejo Comprar" icon={<Heart size={18} weight="fill" />} color="#7209B7" items={wishlist} to="/perfil/lista/wishlist" />
              <CollectionCard title="Já Lidos" icon={<BookOpen size={18} weight="fill" />} color="#10B981" items={readList} to="/perfil/lista/read" />
            </div>
          </div>

          {/* Collection stats */}
          <CollectionStats items={collection} />

          {/* Activity */}
          <div id="activity">
            <SectionTitle
              title="ATIVIDADE RECENTE"
              more={activity.length > 5 ? { label: showAllActivity ? "Mostrar menos" : `Ver toda a atividade (${activity.length})`, to: "#", onClick: () => setShowAllActivity(!showAllActivity) } : null}
            />
            <div className="border border-white/5 rounded-2xl bg-[#0f0f14] divide-y divide-white/5" data-testid="activity-list">
              {activity.length === 0 && (
                <div className="p-8 text-center text-zinc-500 font-mono-accent text-xs">SEM ATIVIDADE AINDA</div>
              )}
              {(showAllActivity ? activity : activity.slice(0, 5)).map((a, idx) => {
                const Icon = ACTIVITY_ICONS[a.icon] || Trophy;
                return (
                  <div key={idx} className="flex items-center gap-4 px-5 py-3.5" data-testid={`activity-${idx}`}>
                    <div className="w-9 h-9 rounded-lg bg-[#E63946]/10 text-[#E63946] grid place-items-center flex-shrink-0">
                      <Icon size={16} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0 text-sm">{a.label}</div>
                    <div className="text-xs text-zinc-500 hidden sm:block">{timeAgo(a.created_at)}</div>
                    <div className="font-display text-sm font-bold text-emerald-400 whitespace-nowrap">+{a.amount} XP</div>
                  </div>
                );
              })}
              {!showAllActivity && activity.length > 5 && (
                <button onClick={() => setShowAllActivity(true)} className="w-full py-3 text-center text-sm text-[#E63946] hover:bg-white/5 font-display font-semibold" data-testid="show-more-activity">
                  Ver mais
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============ RECENT ACHIEVEMENTS (full width) ============ */}
      {progress && (
        <div className="mt-8" onClick={() => setAchievementsOpen(true)} data-testid="open-achievements-dialog">
          <RecentAchievements achievements={progress.achievements} />
        </div>
      )}

      <BannerPicker
        open={bannerOpen}
        onOpenChange={setBannerOpen}
        currentBannerId={user.banner_id}
        onChange={(u) => setUser(u)}
      />
      {progress && (
        <AchievementsDialog
          open={achievementsOpen}
          onOpenChange={setAchievementsOpen}
          achievements={progress.achievements}
        />
      )}
      <FollowListDialog
        open={!!followListType}
        onOpenChange={(v) => { if (!v) setFollowListType(null); }}
        identifier={user.user_id}
        type={followListType || "followers"}
      />
    </div>
  );
}

function ProfileTab({ label, active, onClick, badge, testid }) {
  const cls = active
    ? "text-white border-b-2 border-[#E63946] pb-3 pt-3 px-4 font-display font-semibold text-sm"
    : "text-zinc-400 hover:text-white border-b-2 border-transparent pb-3 pt-3 px-4 font-display font-medium text-sm transition-colors";
  return (
    <button type="button" onClick={onClick} className={cls} data-testid={testid}>
      {label}
      {badge !== undefined && (
        <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-mono-accent font-bold">{badge}</span>
      )}
    </button>
  );
}

function EditProfileDialog({ form, setForm, onSubmit, saving, uploadFile, user, setUser }) {
  return (
    <DialogContent className="bg-[#121217] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Editar Perfil</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 mt-2">
        <div>
          <Label className="font-mono-accent text-[10px] text-zinc-400">NOME</Label>
          <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 bg-[#1C1C22] border-white/10" data-testid="profile-name-input" />
        </div>
        <div>
          <Label className="font-mono-accent text-[10px] text-zinc-400">FOTO DE PERFIL</Label>
          <div className="mt-2 flex items-center gap-3">
            {form.picture && (
              <img src={resolveImageUrl(form.picture)} alt="" className="w-16 h-16 rounded-full object-cover border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />
            )}
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 hover:bg-white/5 text-sm" data-testid="upload-picture-btn">
              <UploadSimple size={16} weight="bold" /> Carregar imagem
              <input type="file" accept="image/*" className="hidden" onChange={uploadFile} disabled={saving} data-testid="upload-picture-input" />
            </label>
          </div>
        </div>
        <div>
          <Label className="font-mono-accent text-[10px] text-zinc-400">OU LINK EXTERNO</Label>
          <Input value={form.picture} onChange={e => setForm({...form, picture: e.target.value})} placeholder="https://..." className="mt-1 bg-[#1C1C22] border-white/10" data-testid="profile-picture-input" />
        </div>
        <div>
          <Label className="font-mono-accent text-[10px] text-zinc-400">DESCRIÇÃO</Label>
          <Textarea rows={3} value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} placeholder="Fala um pouco sobre ti..." className="mt-1 bg-[#1C1C22] border-white/10" data-testid="profile-bio-input" />
        </div>
        <div className="flex items-center justify-between bg-[#1C1C22] border border-white/10 rounded-lg p-3">
          <div>
            <div className="font-display font-semibold flex items-center gap-2">
              {form.is_private ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
              Perfil {form.is_private ? "Privado" : "Público"}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">{form.is_private ? "Outros só veem nome e foto" : "Outros veem o teu perfil completo"}</div>
          </div>
          <button type="button" onClick={() => setForm({...form, is_private: !form.is_private})} data-testid="profile-privacy-toggle" className={`relative w-12 h-6 rounded-full transition-colors ${form.is_private ? "bg-[#E63946]" : "bg-zinc-700"}`}>
            <div className={`absolute top-0.5 ${form.is_private ? "left-[26px]" : "left-0.5"} w-5 h-5 rounded-full bg-white transition-all`} />
          </button>
        </div>
        <ProfileSlugSection user={user} setUser={setUser} />

        <div className="bg-[#1C1C22] border border-white/10 rounded-lg p-3 space-y-3">
          <Label className="font-mono-accent text-[10px] text-zinc-400 block">REDES SOCIAIS</Label>
          <SocialInput label="Instagram" value={form.instagram} onChange={v => setForm({...form, instagram: v})} placeholder="@teu_nome" testid="instagram" />
          <SocialInput label="YouTube" value={form.youtube} onChange={v => setForm({...form, youtube: v})} placeholder="@teu_canal" testid="youtube" />
          <SocialInput label="Facebook" value={form.facebook} onChange={v => setForm({...form, facebook: v})} placeholder="teu.perfil" testid="facebook" />
          <SocialInput label="X (Twitter)" value={form.twitter} onChange={v => setForm({...form, twitter: v})} placeholder="@teu_nome" testid="twitter" />
        </div>

        <Button type="submit" disabled={saving} className="btn-crimson w-full" data-testid="profile-save-btn">
          {saving ? "A guardar..." : "Guardar"}
        </Button>
      </form>
    </DialogContent>
  );
}

function SocialInput({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-zinc-400 text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 bg-[#0f0f14] border-white/10 h-9 text-sm" data-testid={`social-input-${testid}`} />
    </div>
  );
}

function ProfileSlugSection({ user, setUser }) {
  const [slug, setSlug] = useState(user.slug || "");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  let nextAllowed = null;
  let daysLeft = 0;
  if (user.slug_changed_at) {
    try {
      const last = new Date(user.slug_changed_at);
      nextAllowed = new Date(last.getTime() + 30 * 24 * 60 * 60 * 1000);
      daysLeft = Math.ceil((nextAllowed - new Date()) / (24 * 60 * 60 * 1000));
    } catch { /* */ }
  }
  const locked = nextAllowed && nextAllowed > new Date();
  const liveOrigin = "https://portugalmanga.pt";
  const profileUrl = user.slug ? `${liveOrigin}/membros/${user.slug}` : `${liveOrigin}/membros/${user.user_id}`;

  async function save(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!slug.trim()) { toast.error("Indica um URL válido"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/me/slug", { slug: slug.trim() });
      setUser(data);
      toast.success("URL atualizado");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  function copyUrl() {
    navigator.clipboard.writeText(profileUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="bg-[#1C1C22] border border-white/10 rounded-lg p-3" data-testid="profile-slug-card">
      <Label className="font-mono-accent text-[10px] text-zinc-400 flex items-center gap-1">
        <LinkSimple size={12} weight="bold" /> URL DO PERFIL
      </Label>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <code className="text-xs text-zinc-300 bg-black/40 px-2 py-1 rounded border border-white/5 break-all flex-1">{profileUrl}</code>
        <button type="button" onClick={copyUrl} className="text-zinc-400 hover:text-white p-1.5" title="Copiar" data-testid="copy-profile-url">
          {copied ? <Check size={14} weight="bold" className="text-emerald-400" /> : <LinkSimple size={14} weight="bold" />}
        </button>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <div className="flex items-center bg-black/40 border border-white/5 rounded pl-2 flex-1">
          <span className="text-zinc-500 text-xs">/membros/</span>
          <Input value={slug} onChange={(e) => setSlug(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""))} placeholder="oteu_nome" disabled={locked} maxLength={24} className="border-0 bg-transparent focus-visible:ring-0 px-2 h-8 text-sm" data-testid="profile-slug-input" />
        </div>
        <button type="button" onClick={save} disabled={saving || locked || !slug.trim() || slug === user.slug} className="px-3 h-8 rounded btn-crimson text-xs disabled:opacity-40" data-testid="profile-slug-save-btn">
          {saving ? "..." : "Guardar"}
        </button>
      </div>
      <div className="text-[10px] text-zinc-500 mt-1">
        {locked ? `Próxima alteração em ${daysLeft} ${daysLeft === 1 ? "dia" : "dias"}` : "Só uma alteração por mês"}
      </div>
    </div>
  );
}
